import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { PhoneIncoming, PhoneCall, X, User, Home, Briefcase, KeyRound, AlertTriangle } from 'lucide-react';
import { useAuth } from '../App';
import { ClickToCallButton } from './ClickToCallButton';
import { useLocation } from 'react-router-dom';
import { isPublicRoute } from '../utils/publicRoutes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

const OFFICE_ROLES = ['admin', 'customer_service', 'bookings_operator', 'invoice_operator'];

const TYPE_STYLE = {
  guest: { label: 'Ospite', cls: 'bg-indigo-100 text-indigo-700', Icon: User },
  owner: { label: 'Proprietario', cls: 'bg-emerald-100 text-emerald-700', Icon: KeyRound },
  partner: { label: 'Partner', cls: 'bg-amber-100 text-amber-700', Icon: Briefcase },
  manager: { label: 'Responsabile', cls: 'bg-sky-100 text-sky-700', Icon: Home },
};

// ---------- Suoneria: campanello telefonico classico (file WAV in loop) ----------
let _ringAudio = null;
const getRingAudio = () => {
  if (!_ringAudio) {
    _ringAudio = new Audio('/sounds/phone-ring.wav');
    _ringAudio.loop = true;
    _ringAudio.volume = 0.95;
  }
  return _ringAudio;
};

const MatchBlock = ({ m }) => {
  const t = TYPE_STYLE[m.type] || {};
  const Icon = t.Icon || User;
  return (
    <div className="mt-2 border-t border-slate-100 pt-2">
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${t.cls || 'bg-slate-100 text-slate-600'}`}>
          <Icon className="h-3 w-3" /> {t.label || m.type}
        </span>
        <span className="font-semibold text-slate-800 text-sm" data-testid="caller-popup-name">{m.name}</span>
        {m.bookings_count > 1 && <span className="text-[10px] text-slate-400">{m.bookings_count} prenotazioni</span>}
      </div>
      {m.booking && (
        <p className="text-xs text-slate-600 mt-1 ml-1">
          🏠 {m.booking.property_name || '—'}
          {m.booking.checkin_date && <> · {m.booking.checkin_date} → {m.booking.checkout_date}</>}
        </p>
      )}
      {m.booking?.manager_name && (
        <div className="text-xs text-slate-600 mt-0.5 ml-1 flex items-center gap-1.5" data-testid="caller-popup-manager">
          <span>👤 Responsabile: <span className="font-medium">{m.booking.manager_name}</span></span>
          {m.booking.manager_phone && (
            <>
              <span className="text-slate-400">{m.booking.manager_phone}</span>
              <ClickToCallButton number={m.booking.manager_phone} className="h-5 w-5" />
            </>
          )}
        </div>
      )}
      {(m.properties || []).length > 0 && (
        <div className="text-xs text-slate-600 mt-1 ml-1 space-y-0.5">
          {m.properties.slice(0, 4).map((p, i) => (
            <p key={i}>🏠 {p.name}{p.city ? ` · ${p.city}` : ''}</p>
          ))}
          {(m.properties_total || m.properties.length) > 4 && (
            <p className="text-slate-400">+ altre {(m.properties_total || m.properties.length) - 4} case</p>
          )}
        </div>
      )}
    </div>
  );
};

const EmailCapture = ({ ev }) => {
  const [email, setEmail] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  if (ev.caller_email_found !== false || !ev.from_number) return null;
  if (saved) {
    return (
      <p className="mt-2 text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-lg px-2 py-1.5" data-testid="caller-popup-email-saved">
        ✓ Email salvata in rubrica: il riepilogo della chiamata arriverà a questo indirizzo
      </p>
    );
  }
  const save = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email)) return;
    setSaving(true);
    try {
      await axios.post(`${API}/cs-control/contacts/save-email`,
        { phone: ev.from_number, email: email.trim(), name: ev.caller?.matches?.[0]?.name || null },
        { headers: authHeaders() });
      setSaved(true);
    } catch (_) { /* riprova */ }
    setSaving(false);
  };
  return (
    <div className="mt-2 border border-amber-200 bg-amber-50 rounded-lg p-2" data-testid="caller-popup-email-capture">
      <p className="text-[11px] font-semibold text-amber-800">📧 Email non in rubrica — chiedila al cliente per inviargli il riepilogo della chiamata</p>
      <div className="flex gap-1 mt-1.5">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          placeholder="email@cliente.it"
          data-testid="caller-popup-email-input"
          className="flex-1 min-w-0 border border-amber-200 rounded-md px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
        />
        <button
          onClick={save}
          disabled={saving || !/^\S+@\S+\.\S+$/.test(email)}
          data-testid="caller-popup-email-save"
          className="text-xs font-semibold bg-amber-600 text-white rounded-md px-2.5 py-1 disabled:opacity-40 hover:bg-amber-500">
          {saving ? '...' : 'Salva'}
        </button>
      </div>
    </div>
  );
};

export const CallerIdPopup = () => {
  const { user } = useAuth();
  const location = useLocation();
  const publicPage = isPublicRoute(location.pathname);
  const [events, setEvents] = useState([]);
  const [healthAlert, setHealthAlert] = useState(null);
  const [emailModalHidden, setEmailModalHidden] = useState(() => new Set());
  const dismissed = useRef(new Set());
  const notified = useRef(new Set());
  const configured = useRef(true);
  const hasRingingRef = useRef(false);
  const forceCapture = useRef(null);

  const isOffice = !publicPage && user && OFFICE_ROLES.includes(user.role);

  useEffect(() => {
    if (!publicPage) return;
    setEvents([]);
    const audio = getRingAudio();
    audio.pause();
    audio.currentTime = 0;
  }, [publicPage]);

  // Deep-link dalla notifica push "manca l'email": /?email_capture={call_id}
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('email_capture');
    if (p) {
      forceCapture.current = p;
      const url = new URL(window.location.href);
      url.searchParams.delete('email_capture');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, []);

  // Sblocco audio al primo click/tocco (policy autoplay dei browser)
  useEffect(() => {
    if (!isOffice) return undefined;
    const unlock = () => {
      const a = getRingAudio();
      if (hasRingingRef.current) { a.play().catch(() => {}); return; }
      if (a.paused) {
        a.muted = true;
        a.play().then(() => { a.pause(); a.currentTime = 0; a.muted = false; }).catch(() => { a.muted = false; });
      }
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [isOffice]);

  // Permesso notifiche di sistema + sottoscrizione Web Push (ricezione anche ad app chiusa)
  useEffect(() => {
    if (!isOffice || !('Notification' in window)) return;
    const subscribe = () => {
      import('../utils/push').then(({ ensurePushSubscription }) => ensurePushSubscription().catch(() => {}));
    };
    if (Notification.permission === 'default') {
      Notification.requestPermission().then((p) => { if (p === 'granted') subscribe(); }).catch(() => {});
    } else if (Notification.permission === 'granted') {
      subscribe();
    }
  }, [isOffice]);

  const notifySystem = useCallback((ev) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const name = ev.caller?.matches?.[0]?.name;
    try {
      const n = new Notification('📞 Chiamata in arrivo — HW Smart', {
        body: `${ev.from_number || 'Anonimo'}${name ? ` · ${name}` : ''}`,
        tag: `hw-ring-${String(ev.from_number || ev.call_id).replace(/\D/g, '').slice(-9) || ev.call_id}`,
        requireInteraction: true,
        icon: 'https://www.holidayworld.it/wp-content/uploads/2018/05/Logo-new1.png',
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch (_) { /* alcuni browser richiedono service worker */ }
  }, []);

  const poll = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cs-control/voxloud/ring`, { headers: authHeaders() });
      configured.current = r.data.configured !== false;
      setHealthAlert(r.data.webhook_alert || null);
      if (!configured.current) { setEvents([]); return true; }
      const active = (r.data.events || []).filter(ev =>
        ['ringing', 'answered'].includes(ev.status) && !dismissed.current.has(ev.call_id));
      // Gambe di gruppo: se qualcuno ha già risposto (stesso numero), le card "squilla" spariscono;
      // e mai due card per la stessa chiamata/numero
      const norm = (n) => String(n || '').replace(/\D/g, '').slice(-9);
      const answeredNums = new Set(active.filter(ev => ev.status === 'answered').map(ev => norm(ev.from_number)).filter(Boolean));
      const seen = new Set();
      const cleaned = active.filter(ev => {
        const k = norm(ev.from_number);
        if (ev.status === 'ringing' && k && answeredNums.has(k)) return false;
        const dk = `${ev.status}-${k || ev.call_id}`;
        if (seen.has(dk)) return false;
        seen.add(dk);
        return true;
      });
      cleaned.filter(ev => ev.status === 'ringing' && !notified.current.has(norm(ev.from_number) || ev.call_id)).forEach(ev => {
        notified.current.add(norm(ev.from_number) || ev.call_id);
        notifySystem(ev);
      });
      // click sulla notifica push "manca l'email" → riapri il popup centrato anche se era stato chiuso
      if (forceCapture.current && cleaned.some(ev => ev.call_id === forceCapture.current)) {
        const fc = forceCapture.current;
        forceCapture.current = null;
        setEmailModalHidden(prev => { const n = new Set(prev); n.delete(fc); return n; });
      }
      setEvents(cleaned.slice(0, 3));
      return true;
    } catch (error) {
      console.warn('[CallerId] polling non disponibile:', error?.response?.status || error?.message);
      return false;
    }
  }, [notifySystem]);

  // Polling SEMPRE attivo (anche con scheda in background o finestra ridotta a icona)
  useEffect(() => {
    if (!isOffice) return undefined;
    let stopped = false;
    let failures = 0;
    let timer;
    const tick = async () => {
      const available = await poll();
      if (stopped) return;
      failures = available ? 0 : Math.min(failures + 1, 4);
      const delay = available ? 5000 : Math.min(60000, 5000 * (2 ** failures));
      timer = setTimeout(tick, delay);
    };
    tick();
    return () => { stopped = true; clearTimeout(timer); };
  }, [isOffice, poll]);

  // Squillo continuo (non silenziabile) finché c'è una chiamata in stato "ringing":
  // si ferma solo quando si risponde, il chiamante riaggancia o si chiude la card
  const hasRinging = events.some(ev => ev.status === 'ringing');
  hasRingingRef.current = hasRinging;
  useEffect(() => {
    const a = getRingAudio();
    if (hasRinging) {
      a.play().catch(() => { /* audio bloccato: riparte al primo click grazie a unlock */ });
    } else {
      a.pause();
      a.currentTime = 0;
    }
  }, [hasRinging]);

  if (!isOffice || (events.length === 0 && !healthAlert)) return null;

  // Popup CENTRATO: in conversazione con un cliente senza email in rubrica
  const emailModalEv = events.find(ev =>
    ev.status === 'answered' && ev.caller_email_found === false && ev.from_number
    && !emailModalHidden.has(ev.call_id));
  const hideEmailModal = (id) => setEmailModalHidden(prev => new Set(prev).add(id));

  return (
    <>
      {healthAlert && createPortal(
        <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[220] w-[calc(100vw-2rem)] max-w-xl border-2 border-red-400 bg-red-50 text-red-900 shadow-2xl rounded-md p-4 flex items-start gap-3"
          role="alert" data-testid="voxloud-global-health-alert">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-bold">{healthAlert.title}</p>
            <p className="text-sm mt-1">{healthAlert.message}</p>
          </div>
        </div>,
        document.body
      )}
      {emailModalEv && createPortal(
        <div className="fixed inset-0 z-[200] pointer-events-auto flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          data-testid="email-capture-modal">
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-amber-300 w-[440px] max-w-full p-5 animate-in zoom-in-95 duration-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sky-100">
                  <PhoneCall className="h-5 w-5 text-sky-700" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-sky-600">
                    In conversazione{emailModalEv.answered_by ? ` · ${emailModalEv.answered_by}` : ''}
                  </p>
                  <p className="font-bold text-slate-800 text-lg leading-tight" data-testid="email-modal-number">
                    {emailModalEv.from_number}
                  </p>
                  {emailModalEv.caller?.matches?.[0]?.name && (
                    <p className="text-xs text-slate-500">{emailModalEv.caller.matches[0].name}</p>
                  )}
                </div>
              </div>
              <button onClick={() => hideEmailModal(emailModalEv.call_id)}
                data-testid="email-modal-close" className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-3 text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              📧 Questo cliente <b>non ha un'email in rubrica</b>: chiedigliela ora, al termine
              riceverà automaticamente il riepilogo della chiamata.
            </p>
            <EmailCapture ev={emailModalEv} />
            <div className="mt-3 flex justify-end">
              <button onClick={() => hideEmailModal(emailModalEv.call_id)}
                data-testid="email-modal-later"
                className="text-xs font-medium text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-md hover:bg-slate-100">
                Più tardi
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {events.length > 0 && <div className="fixed bottom-4 right-4 z-[90] space-y-2 w-80" data-testid="caller-popup">
      {events.map(ev => {
        const matches = ev.caller?.matches || [];
        return (
          <div key={ev.call_id}
            className={`bg-white rounded-xl shadow-2xl border-2 p-3 animate-in slide-in-from-right ${ev.status === 'ringing' ? 'border-emerald-400' : 'border-sky-300'}`}
            data-testid={`caller-popup-card-${ev.call_id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                {ev.status === 'ringing'
                  ? <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-60 animate-ping" />
                      <PhoneIncoming className="h-4 w-4 text-emerald-700 relative" />
                    </span>
                  : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-100">
                      <PhoneCall className="h-4 w-4 text-sky-700" />
                    </span>}
                <div>
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
                    {ev.status === 'ringing' ? 'Chiamata in arrivo' : `In conversazione${ev.answered_by ? ` · ${ev.answered_by}` : ''}`}
                  </p>
                  <p className="font-bold text-slate-800 text-sm" data-testid="caller-popup-number">{ev.from_number || 'Anonimo'}</p>
                </div>
              </div>
              <button onClick={() => { dismissed.current.add(ev.call_id); setEvents(es => es.filter(x => x.call_id !== ev.call_id)); }}
                data-testid="caller-popup-close" className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            {matches.length === 0
              ? <p className="mt-2 text-xs text-slate-500 border-t border-slate-100 pt-2" data-testid="caller-popup-unknown">Numero non presente in rubrica (nessun ospite, proprietario, partner o responsabile)</p>
              : matches.map((m, i) => <MatchBlock key={i} m={m} />)}
            <EmailCapture ev={ev} />
          </div>
        );
      })}
      </div>}
    </>
  );
};

export default CallerIdPopup;
